import { useMemo, useRef } from "react";
import { padDateRange } from "../utils/dateUtils";

/**
 * Grows the padded range to fit the data and never shrinks or shifts it.
 *
 * The Gantt keeps its pixel scroll offset when it re-initialises, so moving
 * the range start moves the dates under the viewport. Data changes that stay
 * inside the current range therefore keep the very same range object.
 */
export const extendDateRange = (current, data) => {
	if (!data?.start || !data?.end) {
		return current?.start ? current : { start: null, end: null };
	}
	if (!current?.start) return padDateRange(data);
	if (data.start >= current.start && data.end <= current.end) return current;

	// Each edge moves only when data crosses it: wider padding from a longer
	// span must not drag the other edge along
	const padded = padDateRange(data);
	return {
		start: data.start < current.start ? padded.start : current.start,
		end: data.end > current.end ? padded.end : current.end,
	};
};

const useGanttDateRange = (dataRange, resetKey) => {
	const rangeRef = useRef({ key: resetKey, range: null });

	return useMemo(() => {
		const held = rangeRef.current;
		const current = held.key === resetKey ? held.range : null;
		const next = extendDateRange(current, dataRange);
		rangeRef.current = { key: resetKey, range: next };
		return next;
	}, [dataRange, resetKey]);
};

export default useGanttDateRange;
